import React from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  Pagination as PaginationType,
  defaultTheme
} from '@yes.technology/react-toolkit'

import Pagination from '../Pagination'
import SectionDivider from '../SectionDivider'

import ColumnsNavigation from './components/ColumnsNavigation'
import LayoutAndReport from './components/LayoutAndReport/LayoutAndReport'

import { AreaLineCenter } from './DataTable.styles'

import { HierarchyLevel } from './types'
import useTableContext from './hooks/useTableContext'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'

type DataTableAreaLineProps = {
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  pagination?: Partial<PaginationType>
  setCurrentPage?: (page: number) => void
  hierarchyLevel: HierarchyLevel
  title: string
  isShowTitle?: boolean
  isCollapsibleCard?: boolean
  titleOnly?: boolean
  children?: React.ReactNode
  embedded?: boolean
  disabled?: boolean
  hasTitleFading?: boolean
  hideMainCollapsibleContainer?: boolean
  hideCollapsibleCardArrow?: boolean
  allowCollapsibleNavigationCardCustomColors?: boolean
  layoutClassificationUuid?: string
  navSticky?: boolean
} & React.HTMLAttributes<HTMLDivElement>

const primaryColor = (defaultTheme as any).colors.primary || '#006bff'
const whiteColor = '#ffffff'

export default function DataTableAreaLine({
  navigationSliderColor,
  navigationSliderBackgroundColor = '#004099',
  pagination,
  hierarchyLevel,
  title,
  isShowTitle = true,
  isCollapsibleCard = false,
  titleOnly,
  children,
  setCurrentPage = () => '',
  embedded,
  hideMainCollapsibleContainer = false,
  hideCollapsibleCardArrow = false,
  hasTitleFading,
  allowCollapsibleNavigationCardCustomColors = false,
  layoutClassificationUuid,
  navSticky,
  ...htmlProps
}: DataTableAreaLineProps) {
  const { t } = useTranslation()

  const {
    totalColumns,
    columnsNames,
    amountOfActionColumns,
    objects,
    extendMobileAreaLine: extendMobileBreakpoint
  } = useTableContext()

  const breakpoint = useBreakpoint()

  const willColumnsHide =
    objects.length === 0 ||
    totalColumns - (columnsNames.length + amountOfActionColumns) <= 0

  const willPaginationHide = !pagination?.hasPrevious && !pagination?.hasNext

  const willHaveCenterElement = willColumnsHide && willPaginationHide

  const willHaveFading =
    breakpoint === 'sm' || (!willHaveCenterElement && hasTitleFading)

  const completeTitle = isShowTitle
    ? title
      ? `${title} | ${t('area-title.data-tables')}`
      : t('area-title.data-tables')
    : ''

  const getNavigationColor = () => {
    if (isCollapsibleCard) {
      return allowCollapsibleNavigationCardCustomColors
        ? navigationSliderColor
        : whiteColor
    }

    return navigationSliderColor || whiteColor
  }

  const getNavigationBackgroundColor = () => {
    if (isCollapsibleCard) {
      return allowCollapsibleNavigationCardCustomColors
        ? navigationSliderBackgroundColor
        : primaryColor
    }

    return navigationSliderBackgroundColor
  }

  const color = getNavigationColor()

  const backgroundColor = getNavigationBackgroundColor()

  const centerHeaderElement = titleOnly ? undefined : (
    <AreaLineCenter aria-label='Area line center'>
      <ColumnsNavigation
        hierarchyLevel={hierarchyLevel}
        navigationSliderBackgroundColor={backgroundColor}
        navigationSliderColor={color}
      />
      <Pagination
        sectionText={t('data-tables.page')}
        pagination={pagination}
        setCurrentPage={setCurrentPage}
        hierarchyLevel={hierarchyLevel}
        className='d-flex justify-content-sm-center justify-content-lg-end flex-column flex-lg-row'
        navigationSliderBackgroundColor={backgroundColor}
        navigationSliderColor={color}
      />
    </AreaLineCenter>
  )

  return (
    <SectionDivider
      navSticky={navSticky}
      title={completeTitle}
      isCollapsibleCard={isCollapsibleCard}
      centerHeaderElement={centerHeaderElement}
      rightHeaderElement={
        <LayoutAndReport layoutClassificationUuid={layoutClassificationUuid} />
      }
      hasTitleFading={willHaveFading}
      backgroundColor={backgroundColor}
      color={color}
      embedded={embedded}
      extendMobileAreaLine={extendMobileBreakpoint}
      isVisible={!hideMainCollapsibleContainer}
      hideCollapsibleCardArrow={hideCollapsibleCardArrow}
      {...htmlProps}
    >
      {children}
    </SectionDivider>
  )
}
